import React, { useState, useEffect, useRef } from 'react';
import Turnstile from '../components/Turnstile';
import { useNavigate, useLocation, Link } from 'react-router-dom';
import Country from 'country-state-city/lib/country';
import { useAuth } from '../context/AuthContext';
import { Alert, Spinner } from '../components/common/index';
import { useAutoFitPlaceholders } from '../utils/fitPlaceholder'; // grey hints shrink to fit their box (4 Oct 2026)
import { MascotLogo } from '../components/common/ThyroDoctorMascot'; // mascot logo replaces the green icon (4 Oct 2026)
import { patientAPI, authAPI } from '../api';
import { takeMobileInput, mobileSubmitError, MobileError, usePhoneRules, phoneRuleFor, mobilePlaceholder } from '../utils/mobile';
import { passwordProblem, PASSWORD_HINT } from '../utils/passwordPolicy';
import { useAuthConfig } from '../utils/authConfig'; // email-only launch switch (M1, 13 Oct 2026)


// English / Hindi (13 Oct 2026, Hindi version Batch 1b)
import { useI18n, UI_LANGUAGES, translate, isLangTouched } from '../i18n';

// Doctor sign-in stays English, whatever language the patient side shows.
const tEnglish = (text, vars) => translate('en', text, vars);
// Language list: the platform's single list, loaded from the backend
// (src/constants/languages.js → GET /api/public/languages). Edit it only in
// the backend's src/config/languages.js.

// HOLD (Sept 2026): language switching disabled platform-wide until full
// registration-through-opinion translation testing is complete. Named to
// match the identically-purposed flag in PatientPortal.js / RegisterPage.js,
// though this page's behaviour while off is deliberately different from
// theirs: those hide their picker entirely, but here it stays visible,
// forced to English, and disabled — so a first-time visitor still sees
// that ten languages exist without being misled into thinking they can
// pick one right now. preferredLanguage stays 'en' and languageTouched can
// never become true while disabled — so the post-login updateLanguage()
// call below never fires and an existing saved preference is never
// disturbed.
//
// 13 Oct 2026 (Hindi version): switching is ON again for the languages the
// website itself can be shown in (English, Hindi — i18n/index.js). The
// choice changes this page at once; after a patient signs in it is also
// saved on the account when they changed it in this tab.
const LANGUAGE_SWITCHING_ENABLED = true;

// Admin no longer appears anywhere on this public page (see App.js —
// admin sign-in now lives at the separate, unlisted /admin/login route).
// This page now only ever renders 'patient' or 'doctor'.
//
// "Keep as default" (doctor view only): if a doctor checks this box, the
// login page opens straight to the Doctor form on every future visit —
// including after logout/re-login — via this localStorage key. Unchecking
// clears it, reverting the default back to Patient.
const DEFAULT_ROLE_KEY = 'thyroconsult_login_default_role';

// Which dedicated entry point (see App.js: /login/thyroidproblems vs
// /login/otherproblems) was last used to log in, on THIS browser — not a
// permanent per-account setting. ConditionSelection.js reads this to
// filter what "+ Add Condition" offers. Plain /login (channel prop
// absent) never writes this key, so a patient who's only ever used the
// generic login page keeps seeing every condition, unchanged from before.
const LOGIN_CHANNEL_KEY = 'thyro_login_channel';

// Password rule (1 Oct 2026): utils/passwordPolicy.js — same as the server.

// Same country list source as RegisterPage.js (country-state-city — static
// offline dataset, no API key/network call). Used here only to build the
// mobile-number country-code dropdown; India pinned as the default
// selection per spec.
const COUNTRY_LIST = Country.getAllCountries();
const DEFAULT_COUNTRY_ISO = 'IN';

const LoginPage = ({ channel }) => {
  const { t: tUi, chosenLang, setLang } = useI18n();
  useAutoFitPlaceholders();
  // Remember which dedicated entry point brought them here, for
  // ConditionSelection.js to read later — see LOGIN_CHANNEL_KEY above.
  // Only writes when actually reached via one of those two routes;
  // plain /login leaves whatever was last remembered untouched.
  useEffect(() => {
    if (channel === 'thyroid' || channel === 'other') {
      localStorage.setItem(LOGIN_CHANNEL_KEY, channel);
    }
  }, [channel]);

  const [role, setRole] = useState(() => (
    localStorage.getItem(DEFAULT_ROLE_KEY) === 'doctor' ? 'doctor' : 'patient'
  ));
  // Patient view in the chosen language (English / Hindi); doctor view in English.
  const t = role === 'patient' ? tUi : tEnglish;
  // Email-only launch (M1, 13 Oct 2026): patients sign in and reset the
  // password by email; a mobile number not verified by SMS can't sign in.
  const { mobileOtpRequired } = useAuthConfig();
  const emailOnly = role === 'patient' && !mobileOtpRequired;
  const [keepDoctorDefault, setKeepDoctorDefault] = useState(() => (
    localStorage.getItem(DEFAULT_ROLE_KEY) === 'doctor'
  ));
  // Login identifier is now mobile OR email (two separate boxes) rather
  // than one free-text field — whichever one is filled in is used; if
  // both somehow are, mobile takes precedence. See handleSubmit.
  const [mobileCountry, setMobileCountry] = useState(DEFAULT_COUNTRY_ISO);
  const [mobileNumber, setMobileNumber] = useState('');
  const [mobileNumberError, setMobileNumberError] = useState(''); // live rule message (utils/mobile.js)
  const phoneRules = usePhoneRules();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  // Turnstile ("I am human"): on the login form for every sign-in
  // (7 Oct 2026 — was only after 2 wrong passwords); always on
  // forgot-password. Each pass works once, so it renews after a failed
  // sign-in. If the box can't load (loginTsError), Sign in is still allowed
  // and the server decides.
  const [loginToken, setLoginToken] = useState('');
  const [loginTsError, setLoginTsError] = useState('');
  const loginTs = useRef(null);
  const [resetCaptcha, setResetCaptcha] = useState('');
  const resetTs = useRef(null);
  // Login-screen language picker: we don't know who's logging in until
  // after they submit, so this can't show their actual current
  // preference. languageTouched tracks whether they consciously picked
  // something this session — only synced to their profile if true, so
  // an untouched default never silently overwrites an existing saved
  // preference (e.g. a patient who already has Hindi set, logging in
  // without touching this dropdown, must NOT get reset to English).
  const [preferredLanguage, setPreferredLanguage] = useState('en');
  const [languageTouched, setLanguageTouched] = useState(false);
  const { login } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();

  // ── Password reset (Sep 2026 — new) ──────────────────────────────────
  // 'login' (default) | 'forgot-choose' | 'forgot-mobile-otp' |
  // 'forgot-email-sent' | 'forgot-set-new-password' | 'forgot-done'.
  // 'forgot-set-new-password' is entered two ways: normally reached from
  // 'forgot-mobile-otp' after a correct OTP, OR directly on page load if
  // the URL carries a ?token= query param — i.e. the person clicked the
  // emailed reset link. Ideally that link lands on its own dedicated
  // /reset-password route rather than this same /login page reading a
  // query param — this file doesn't have visibility into App.js's router
  // config, so wire an actual /reset-password route to this component
  // (or move this block into its own component) if that's not already
  // how it's set up.
  const [view, setView] = useState('login');
  const [forgotChannel, setForgotChannel] = useState('mobile'); // 'mobile' | 'email'
  const [forgotMobileCountry, setForgotMobileCountry] = useState(DEFAULT_COUNTRY_ISO);
  const [forgotMobile, setForgotMobile] = useState('');
  const [forgotMobileError, setForgotMobileError] = useState('');
  const [forgotEmail, setForgotEmail] = useState('');
  const [forgotOtp, setForgotOtp] = useState('');
  const [resetToken, setResetToken] = useState(null);
  const [newPassword, setNewPassword] = useState('');
  const [confirmNewPassword, setConfirmNewPassword] = useState('');
  const [forgotLoading, setForgotLoading] = useState(false);
  const [forgotError, setForgotError] = useState('');

  // Doctor self-registration moved to its own page, /doctor/register
  // (pages/doctor/DoctorRegisterPage.js, 24 Sep 2026).

  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const token = params.get('token');
    if (token) {
      setResetToken(token);
      setView('forgot-set-new-password');
    } else if (params.get('forgot') === '1') {
      // From "Forgot password?" in the Confirm-it's-you box (8 Oct 2026)
      setView('forgot-choose');
    }
  }, [location.search]);

  // Email-only launch: the reset always goes by email (M1, 13 Oct 2026)
  useEffect(() => { if (!mobileOtpRequired) setForgotChannel('email'); }, [mobileOtpRequired, view]);
  const resetForgotState = () => {
    setView('login');
    setForgotChannel(mobileOtpRequired ? 'mobile' : 'email');
    setForgotMobile(''); setForgotEmail(''); setForgotOtp('');
    setResetToken(null);
    setNewPassword(''); setConfirmNewPassword('');
    setForgotError('');
  };

  // Step 1 of either path — request the reset.
  const handleRequestReset = async () => {
    setForgotError('');
    if (forgotChannel === 'mobile' && !forgotMobile.trim()) {
      return setForgotError('Please enter your mobile number');
    }
    if (forgotChannel === 'mobile') {
      const mErr = mobileSubmitError(forgotMobile, phoneRuleFor(phoneRules, forgotMobileCountry));
      if (mErr) { setForgotMobileError(mErr); return; }
    }
    if (forgotChannel === 'email' && !/\S+@\S+\.\S+/.test(forgotEmail)) {
      return setForgotError('Please enter a valid email address');
    }
    setForgotLoading(true);
    try {
      const country = COUNTRY_LIST.find(c => c.isoCode === forgotMobileCountry);
      const dialCode = country ? `+${country.phonecode}` : '+91';
      await authAPI.requestPasswordReset(
        forgotChannel === 'mobile'
          ? { mobileCountryCode: dialCode, mobile: forgotMobile.trim(), turnstileToken: resetCaptcha || undefined }
          : { email: forgotEmail.trim(), turnstileToken: resetCaptcha || undefined }
      );
      setView(forgotChannel === 'mobile' ? 'forgot-mobile-otp' : 'forgot-email-sent');
    } catch (err) {
      // Deliberately still advance to the "check your phone/email" state
      // even on an error — requestPasswordReset's own backend always
      // returns success-shaped responses for anti-enumeration reasons
      // (see its comment in authController.js), so a real error here is
      // almost always a network/server problem, not "account not found".
      setForgotError(err.response?.data?.error || 'Something went wrong. Please try again.');
    } finally {
      setForgotLoading(false);
      setResetCaptcha('');
      if (resetTs.current) resetTs.current.reset(); // each check works once
    }
  };

  // Step 2, mobile path — OTP + new password in one call.
  const handleResetWithOtp = async () => {
    setForgotError('');
    if (!forgotOtp.trim()) return setForgotError('Please enter the OTP');
    { const pw = passwordProblem(newPassword); if (pw) return setForgotError(pw); }
    if (newPassword !== confirmNewPassword) return setForgotError('Passwords do not match');
    setForgotLoading(true);
    try {
      const country = COUNTRY_LIST.find(c => c.isoCode === forgotMobileCountry);
      const dialCode = country ? `+${country.phonecode}` : '+91';
      await authAPI.resetPasswordWithOtp({
        mobileCountryCode: dialCode, mobile: forgotMobile.trim(),
        otp: forgotOtp.trim(), newPassword,
      });
      setView('forgot-done');
    } catch (err) {
      setForgotError(err.response?.data?.error || 'Could not reset password. Please try again.');
    } finally {
      setForgotLoading(false);
    }
  };

  // Step 2, email path — token (from the URL) + new password.
  const handleResetWithToken = async () => {
    setForgotError('');
    { const pw = passwordProblem(newPassword); if (pw) return setForgotError(pw); }
    if (newPassword !== confirmNewPassword) return setForgotError('Passwords do not match');
    setForgotLoading(true);
    try {
      await authAPI.resetPasswordWithToken({ token: resetToken, newPassword });
      setView('forgot-done');
    } catch (err) {
      setForgotError(err.response?.data?.error || 'Could not reset password. This link may have expired.');
    } finally {
      setForgotLoading(false);
    }
  };


  // Switching Patient/Doctor must not carry over whatever was typed under
  // the previous view — clears every credential field/error every time
  // the view changes. Does NOT touch the "keep as default" localStorage
  // preference — that's only changed via the checkbox itself, independent
  // of a one-off manual toggle.
  const handleRoleChange = (val) => {
    setRole(val);
    setMobileCountry(DEFAULT_COUNTRY_ISO);
    setMobileNumber('');
    setEmail('');
    setPassword('');
    setShowPassword(false);
    setError('');
    setLanguageTouched(false);
    setPreferredLanguage('en');
  };

  const handleKeepDefaultChange = (checked) => {
    setKeepDoctorDefault(checked);
    if (checked) localStorage.setItem(DEFAULT_ROLE_KEY, 'doctor');
    else localStorage.removeItem(DEFAULT_ROLE_KEY);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');

    let identifier = '';
    if (mobileNumber.trim() && !emailOnly) {
      const mErr = mobileSubmitError(mobileNumber, phoneRuleFor(phoneRules, mobileCountry));
      if (mErr) { setMobileNumberError(mErr); return; }
      const country = COUNTRY_LIST.find(c => c.isoCode === mobileCountry);
      const dialCode = country ? `+${country.phonecode}` : '+91';
      // FIXED (Sept 2026): delimiter added between dial code and number
      // to match registerPatientStep1's hash construction — without it,
      // two different (dial code, number) pairs of different lengths
      // could concatenate to the same string, wrongly colliding two
      // different people's numbers. See that function's comment for
      // the full explanation. This is still one joined string passed to
      // the existing login(identifier, password, role) call below —
      // fully separating them all the way through AuthContext.js's login
      // function would need that file, which hasn't been provided.
      identifier = `${dialCode}|${mobileNumber.trim()}`;
    } else if (email.trim()) {
      identifier = email.trim();
    }
    if (!identifier) {
      setError(emailOnly ? 'Please enter your email' : 'Please enter your mobile number or email');
      return;
    }

    setLoading(true);
    // Back to the page that asked for the login, when it belongs to this
    // kind of account; otherwise the dashboard (audit 9 Oct 2026).
    const backTo = (prefix) => {
      const from = location.state && location.state.from;
      if (from && typeof from.pathname === 'string' && from.pathname.startsWith(prefix)) {
        return from.pathname + (from.search || '');
      }
      return prefix + 'dashboard';
    };
    // A registration left open in this tab must not answer for this login
    // (10 Oct 2026): a wrong password then refreshed that session and
    // re-sent the login, showing a security-check error instead.
    if (!sessionStorage.getItem('thyro_user')) authAPI.clearTokens();
    try {
      const user = await login(identifier, password, role, loginToken || undefined);
      if (user.role === 'patient') {
        if (languageTouched || isLangTouched()) {
          // Best-effort — a failure here shouldn't block a successful
          // login. By this point login() has already set tokens, so
          // this call is authenticated as this patient.
          patientAPI.updateLanguage(languageTouched ? preferredLanguage : chosenLang).catch(() => {});
        }
        navigate(backTo('/patient/'), { replace: true });
      }
      else navigate(backTo('/doctor/'), { replace: true });
    } catch (err) {
      const msg = err.response?.data?.error || 'Login failed. Please check your credentials.';
      setError(msg);
      setLoginToken(''); // a used check token is never sent again (10 Oct 2026)
      if (loginTs.current) loginTs.current.reset(); // each check works once
    } finally { setLoading(false); }
  };

  return (
    <div style={{ minHeight: '100vh', background: 'var(--bg)', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 24 }}>
      <div style={{ width: '100%', maxWidth: 420 }}>
        {view === 'login' && (
        <>
        {/* Logo & title */}
        {/* "Welcome" sits midway between the title and the language box:
            the same 24px above and below it (14 Oct 2026) */}
        <div style={{ textAlign: 'center', marginBottom: 24 }}>
          <MascotLogo height={44} fontSize={20} />
          <h1 style={{ margin: '24px 0 0', fontSize: '1.8rem', lineHeight: 1.2, color: 'var(--text-primary)' }}>{t('Welcome')}</h1>
          {channel === 'thyroid' && (
            <p style={{ marginTop: 6, fontSize: 13, color: 'var(--text-secondary)' }}>{t('Thyroid Problems — Patient Login')}</p>
          )}
          {channel === 'other' && (
            <p style={{ marginTop: 6, fontSize: 13, color: 'var(--text-secondary)' }}>{t('Other Problems — Patient Login')}</p>
          )}
        </div>

        {/* Language picker — patient-only, per the platform rule that
            physician portals stay English-only, always. Untouched =
            leave whatever's already saved on their profile alone (see
            languageTouched above). Styled to match the previous tab
            strip: light grey pill container, white/surface background
            for the control itself, light blue on hover. */}
        {role === 'patient' && (
          // "Choose language" on top, the globe left of the list (14 Oct 2026)
          <div style={{
            display: 'flex', flexDirection: 'column', gap: 4,
            background: 'var(--gray-100)', borderRadius: 'var(--radius-md)',
            padding: 4, marginBottom: 28,
          }}>
            {/* centred, dark black, not bold (14 Oct 2026) */}
            <span style={{ fontSize: 13, fontWeight: 400, color: '#000000', textAlign: 'center', paddingTop: 2 }}>
              {t('Choose language')}
            </span>
            <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
            <span aria-hidden="true" style={{ paddingLeft: 6, fontSize: 15, lineHeight: 1 }}>🌐</span>
            <select
              value={LANGUAGE_SWITCHING_ENABLED ? chosenLang : 'en'}
              onChange={e => { setLang(e.target.value); setPreferredLanguage(e.target.value); setLanguageTouched(true); }}
              aria-label={t('Choose language')}
              disabled={!LANGUAGE_SWITCHING_ENABLED}
              title={!LANGUAGE_SWITCHING_ENABLED ? t('Language selection is temporarily unavailable') : undefined}
              style={{
                flex: '1 1 140px', minWidth: 0, border: 'none', borderRadius: 8, padding: '8px 10px',
                fontSize: 13, fontWeight: 500, color: 'var(--teal-600)',
                background: 'var(--surface)', boxShadow: 'var(--shadow-sm)',
                cursor: LANGUAGE_SWITCHING_ENABLED ? 'pointer' : 'not-allowed',
                opacity: LANGUAGE_SWITCHING_ENABLED ? 1 : 0.6,
                transition: 'background 0.15s',
              }}
              onMouseEnter={e => { if (LANGUAGE_SWITCHING_ENABLED) e.currentTarget.style.background = 'var(--blue-50)'; }}
              onMouseLeave={e => { if (LANGUAGE_SWITCHING_ENABLED) e.currentTarget.style.background = 'var(--surface)'; }}
            >
              {UI_LANGUAGES.map(l => <option key={l.code} value={l.code}>{l.label}</option>)}
            </select>
            </div>
          </div>
        )}

        {/* Form */}
        <div className="card" style={{ padding: 28 }}>
          {error && <Alert type="error" onClose={() => setError('')}>{t(error)}</Alert>}
          <form onSubmit={handleSubmit}>
            <div className="form-group">
              <label className="form-label">{emailOnly ? t('Login with your E-mail') : t('Login with either your E-mail or mobile number')}</label>
              <input
                className="form-input"
                type="email"
                value={email}
                onChange={e => setEmail(e.target.value)}
                aria-label={t('Email address')}
                autoFocus
                style={{ marginBottom: 10 }}
              />
              {!emailOnly && (<>
              <div style={{ textAlign: 'center', fontSize: 11, color: 'var(--text-tertiary)', margin: '2px 0 10px' }}>{t('or')}</div>
              <div style={{ display: 'flex', gap: 8 }}>
                <select
                  className="form-input"
                  style={{ maxWidth: 150 }}
                  value={mobileCountry}
                  onChange={e => setMobileCountry(e.target.value)}
                  aria-label={t('Country code')}
                >
                  {COUNTRY_LIST.map(c => (
                    <option key={c.isoCode} value={c.isoCode}>+{c.phonecode} ({c.name})</option>
                  ))}
                </select>
                <input
                  className="form-input"
                  type="tel"
                  inputMode="numeric"
                  placeholder={t(mobilePlaceholder(phoneRuleFor(phoneRules, mobileCountry)))}
                  value={mobileNumber}
                  onChange={e => { const r = takeMobileInput(e.target.value, mobileNumber, phoneRuleFor(phoneRules, mobileCountry)); setMobileNumber(r.value); setMobileNumberError(r.error); }}
                  aria-label={t('Mobile number')}
                />
              </div>
              <MobileError message={mobileNumberError} />
              </>)}
            </div>
            <div className="form-group" style={{ marginBottom: 24 }}>
              <label className="form-label">{t('Password')}</label>
              <div style={{ position: 'relative' }}>
                <input className="form-input" type={showPassword ? 'text' : 'password'} value={password} onChange={e => setPassword(e.target.value)} required style={{ paddingRight: 44 }} />
                <button type="button" onClick={() => setShowPassword(!showPassword)} style={{ position: 'absolute', right: 12, top: '50%', transform: 'translateY(-50%)', background: 'none', border: 'none', cursor: 'pointer', color: 'var(--text-tertiary)', fontSize: 14 }}>
                  {showPassword ? '🙈' : '👁'}
                </button>
              </div>
              {/* Password reset (Sep 2026 — new) — patient-only; doctor
                  accounts are admin-created with no self-service reset. */}
              {role === 'patient' && (
                <div style={{ textAlign: 'right', marginTop: 6 }}>
                  <button type="button" onClick={() => setView('forgot-choose')} style={{ background: 'none', border: 'none', padding: 0, cursor: 'pointer', color: 'var(--teal-600)', fontWeight: 500, fontSize: 12, textDecoration: 'none' }}>
                    {t('Forgot password?')}
                  </button>
                </div>
              )}
            </div>
            {/* "I am human" — every sign-in (7 Oct 2026) */}
            <div style={{ marginBottom: 14 }}>
              <Turnstile ref={loginTs} onToken={(tok) => { setLoginToken(tok); if (tok) setLoginTsError(''); }} onError={(c) => setLoginTsError(String(c || 'error'))} action="login" appearance="always" />
              {loginToken ? (
                <div style={{ fontSize: 12, color: '#15803d', fontWeight: 500 }}>{t('✓ Security check complete')}</div>
              ) : loginTsError ? (
                <div style={{ fontSize: 12, color: '#b91c1c' }}>
                  {t('The security check could not load (code {code}).', { code: loginTsError })}{' '}
                  <button type="button" className="btn btn-ghost btn-sm" onClick={() => { setLoginTsError(''); if (loginTs.current) loginTs.current.reset(); }}>{t('Try again')}</button>
                </div>
              ) : (
                <div style={{ fontSize: 12, color: 'var(--text-tertiary)' }}>{t('Please wait for the security check to complete.')}</div>
              )}
            </div>
            <button type="submit" className="btn btn-primary btn-lg" style={{ width: '100%', justifyContent: 'center' }} disabled={loading || (!loginToken && !loginTsError)}>
              {loading ? <Spinner size={18} borderColor="#000" stripColor="#fff" /> : t('Sign in')}
            </button>

            {/* Doctor-only: "Keep as default" sits right below Sign in,
                right-aligned (Aug 16 2026 spec) — not centred, and not
                grouped with the "New Doctor?" line below. */}
            {role === 'doctor' && (
              <label style={{ display: 'flex', alignItems: 'center', gap: 8, justifyContent: 'flex-end', fontSize: 12, color: 'var(--text-tertiary)', marginTop: 12, cursor: 'pointer' }}>
                <input type="checkbox" checked={keepDoctorDefault} onChange={e => handleKeepDefaultChange(e.target.checked)} style={{ margin: 0 }} />
                {t('Keep as default')}
              </label>
            )}
          </form>

          {role === 'patient' ? (
            <>
              <p style={{ textAlign: 'center', fontSize: 13, color: 'var(--text-tertiary)', marginTop: 20 }}>
                {t('New patient?')}{' '}
                <Link to="/register" style={{ color: 'var(--teal-600)', fontWeight: 500, textDecoration: 'none' }}>{t('Create account')}</Link>
              </p>
              <p style={{ textAlign: 'center', fontSize: 13, marginTop: 12 }}>
                <button type="button" onClick={() => handleRoleChange('doctor')} style={{ background: 'none', border: 'none', padding: 0, cursor: 'pointer', color: 'var(--teal-600)', fontWeight: 500, fontSize: 13, textDecoration: 'none' }}>
                  {t('Login as Doctor →')}
                </button>
              </p>
            </>
          ) : (
            <>
              {/* Doctor self-registration: its own page, /doctor/register
                  (pages/doctor/DoctorRegisterPage.js, 24 Sep 2026). */}
              <p style={{ textAlign: 'center', fontSize: 13, color: 'var(--text-tertiary)', marginTop: 20 }}>
                {t('New Doctor?')}{' '}
                <Link to="/doctor/register" style={{ color: 'var(--teal-600)', fontWeight: 500, fontSize: 13, textDecoration: 'none' }}>
                  {t('Create account')}
                </Link>
              </p>
              <p style={{ textAlign: 'center', fontSize: 13, marginTop: 14 }}>
                <button type="button" onClick={() => handleRoleChange('patient')} style={{ background: 'none', border: 'none', padding: 0, cursor: 'pointer', color: 'var(--teal-600)', fontWeight: 500, fontSize: 13, textDecoration: 'none' }}>
                  {t('Login as Patient →')}
                </button>
              </p>
            </>
          )}
        </div>
        </>
        )}

        {/* ── Forgot password views (Sep 2026 — new) ──────────────────── */}
        {view === 'forgot-choose' && (
          <div className="card" style={{ padding: 28 }}>
            <h3 style={{ marginBottom: 6 }}>{t('Reset your password')}</h3>
            <p style={{ fontSize: 13, color: 'var(--text-secondary)', marginBottom: 20 }}>
              {mobileOtpRequired
                ? t('Enter your mobile number to get a one-time code, or your email to get a reset link.')
                : t('Enter your email to get a reset link.')}
            </p>
            {forgotError && <Alert type="error" onClose={() => setForgotError('')}>{t(forgotError)}</Alert>}

            {mobileOtpRequired && (
              <div style={{ display: 'flex', gap: 8, marginBottom: 16 }}>
                <button type="button" className={forgotChannel === 'mobile' ? 'btn btn-primary btn-sm' : 'btn btn-secondary btn-sm'} onClick={() => setForgotChannel('mobile')}>{t('Mobile')}</button>
                <button type="button" className={forgotChannel === 'email' ? 'btn btn-primary btn-sm' : 'btn btn-secondary btn-sm'} onClick={() => setForgotChannel('email')}>{t('Email')}</button>
              </div>
            )}

            {forgotChannel === 'mobile' ? (
              <div className="form-group">
                <label className="form-label">{t('Mobile number')}</label>
                <div style={{ display: 'flex', gap: 8 }}>
                  <select className="form-input" style={{ maxWidth: 150 }} value={forgotMobileCountry} onChange={e => setForgotMobileCountry(e.target.value)}>
                    {COUNTRY_LIST.map(c => <option key={c.isoCode} value={c.isoCode}>+{c.phonecode} ({c.name})</option>)}
                  </select>
                  <input className="form-input" type="tel" inputMode="numeric" value={forgotMobile} onChange={e => { const r = takeMobileInput(e.target.value, forgotMobile, phoneRuleFor(phoneRules, forgotMobileCountry)); setForgotMobile(r.value); setForgotMobileError(r.error); }} autoFocus />
                </div>
                <MobileError message={forgotMobileError} />
              </div>
            ) : (
              <div className="form-group">
                <label className="form-label">{t('Email address')}</label>
                <input className="form-input" type="email" value={forgotEmail} onChange={e => setForgotEmail(e.target.value)} autoFocus />
              </div>
            )}

            <Turnstile ref={resetTs} onToken={setResetCaptcha} action="forgot_password" />
            <button type="button" className="btn btn-primary btn-lg" style={{ width: '100%', justifyContent: 'center', marginTop: 8 }} onClick={handleRequestReset} disabled={forgotLoading}>
              {forgotLoading ? <Spinner size={18} borderColor="#000" stripColor="#fff" /> : (forgotChannel === 'mobile' ? t('Send OTP') : t('Send reset link'))}
            </button>
            <button type="button" onClick={resetForgotState} style={{ background: 'none', border: 'none', padding: 0, cursor: 'pointer', color: 'var(--text-tertiary)', fontSize: 12, marginTop: 14, display: 'block', textAlign: 'center', width: '100%' }}>
              {t('← Back to login')}
            </button>
          </div>
        )}

        {view === 'forgot-mobile-otp' && (
          <div className="card" style={{ padding: 28 }}>
            <h3 style={{ marginBottom: 6 }}>{t('Enter the code')}</h3>
            <p style={{ fontSize: 13, color: 'var(--text-secondary)', marginBottom: 20 }}>
              {t('We sent a one-time code to your mobile number. Enter it below along with your new password.')}
            </p>
            {forgotError && <Alert type="error" onClose={() => setForgotError('')}>{t(forgotError)}</Alert>}

            <div className="form-group">
              <label className="form-label">{t('One-time code')}</label>
              <input className="form-input" type="text" inputMode="numeric" value={forgotOtp} onChange={e => setForgotOtp(e.target.value)} autoFocus />
            </div>
            <div className="form-group">
              <label className="form-label">{t('New password')}</label>
              <input className="form-input" type="password" value={newPassword} onChange={e => setNewPassword(e.target.value)} placeholder={t(PASSWORD_HINT)} />
            </div>
            <div className="form-group" style={{ marginBottom: 20 }}>
              <label className="form-label">{t('Confirm new password')}</label>
              <input className="form-input" type="password" value={confirmNewPassword} onChange={e => setConfirmNewPassword(e.target.value)} />
            </div>

            <button type="button" className="btn btn-primary btn-lg" style={{ width: '100%', justifyContent: 'center' }} onClick={handleResetWithOtp} disabled={forgotLoading}>
              {forgotLoading ? <Spinner size={18} borderColor="#000" stripColor="#fff" /> : t('Reset password')}
            </button>
            <button type="button" onClick={resetForgotState} style={{ background: 'none', border: 'none', padding: 0, cursor: 'pointer', color: 'var(--text-tertiary)', fontSize: 12, marginTop: 14, display: 'block', textAlign: 'center', width: '100%' }}>
              {t('← Back to login')}
            </button>
          </div>
        )}

        {view === 'forgot-email-sent' && (
          <div className="card" style={{ padding: 28, textAlign: 'center' }}>
            <div style={{ fontSize: 36, marginBottom: 12 }}>📧</div>
            <h3 style={{ marginBottom: 8 }}>{t('Check your email')}</h3>
            <p style={{ fontSize: 13, color: 'var(--text-secondary)', marginBottom: 20 }}>
              {t('If that email is registered, we\'ve sent a link to reset your password. The link expires in 60 minutes and can only be used once.')}
            </p>
            <button type="button" onClick={resetForgotState} style={{ background: 'none', border: 'none', padding: 0, cursor: 'pointer', color: 'var(--teal-600)', fontWeight: 500, fontSize: 13, textDecoration: 'none' }}>
              {t('← Back to login')}
            </button>
          </div>
        )}

        {view === 'forgot-set-new-password' && (
          <div className="card" style={{ padding: 28 }}>
            <h3 style={{ marginBottom: 6 }}>{t('Set a new password')}</h3>
            {forgotError && <Alert type="error" onClose={() => setForgotError('')}>{t(forgotError)}</Alert>}
            <div className="form-group">
              <label className="form-label">{t('New password')}</label>
              <input className="form-input" type="password" value={newPassword} onChange={e => setNewPassword(e.target.value)} placeholder={t(PASSWORD_HINT)} autoFocus />
            </div>
            <div className="form-group" style={{ marginBottom: 20 }}>
              <label className="form-label">{t('Confirm new password')}</label>
              <input className="form-input" type="password" value={confirmNewPassword} onChange={e => setConfirmNewPassword(e.target.value)} />
            </div>
            <button type="button" className="btn btn-primary btn-lg" style={{ width: '100%', justifyContent: 'center' }} onClick={handleResetWithToken} disabled={forgotLoading}>
              {forgotLoading ? <Spinner size={18} borderColor="#000" stripColor="#fff" /> : t('Reset password')}
            </button>
          </div>
        )}

        {view === 'forgot-done' && (
          <div className="card" style={{ padding: 28, textAlign: 'center' }}>
            <div style={{ fontSize: 36, marginBottom: 12 }}>✅</div>
            <h3 style={{ marginBottom: 8 }}>{t('Password updated')}</h3>
            <p style={{ fontSize: 13, color: 'var(--text-secondary)', marginBottom: 20 }}>
              {t('Your password has been reset. You can now sign in with your new password.')}
            </p>
            <button type="button" className="btn btn-primary btn-lg" style={{ width: '100%', justifyContent: 'center' }} onClick={resetForgotState}>
              {t('Back to login')}
            </button>
          </div>
        )}

        {/* ── Doctor self-registration views (Sep 2026 — new) ─────────── */}

      </div>
    </div>
  );
};

export default LoginPage;
